import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Settings2, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDateTime } from '@/lib/utils/date';
import { useAclSettingList, useDeleteAclSetting } from '../api';
import type { AclSettingRow } from '../types';

export function AclSettingListPage() {
  const navigate = useNavigate();
  const list = useAclSettingList();
  const del = useDeleteAclSetting();
  const rows = list.data ?? [];

  const [pendingDelete, setPendingDelete] = useState<AclSettingRow | null>(null);

  const columns = useMemo<ColumnDef<AclSettingRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.id}
          </span>
        ),
      },
      {
        accessorKey: 'aclName',
        header: 'Role name',
        cell: ({ row }) => (
          <Link
            to={`/update-acl-settings/${row.original.id}`}
            className="font-medium text-foreground hover:underline"
          >
            {row.original.aclName}
          </Link>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 120,
        cell: ({ row }) => {
          const active = row.original.status === 1;
          return (
            <Badge
              variant="outline"
              className={
                active
                  ? 'border-emerald-300 bg-emerald-50 text-emerald-900'
                  : 'border-muted-foreground/40 text-muted-foreground'
              }
            >
              {active ? 'Active' : 'Inactive'}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        size: 200,
        cell: ({ row }) =>
          row.original.createdAt
            ? formatDateTime(row.original.createdAt)
            : '—',
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                aria-label="Row actions"
              >
                <Settings2 className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-44">
              <DropdownMenuLabel>{row.original.aclName}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() =>
                  navigate(`/update-acl-settings/${row.original.id}`)
                }
              >
                <Pencil className="h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setPendingDelete(row.original)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ],
    [navigate],
  );

  return (
    <FillPage>
      <PageHeader
        title="Roles"
        description="Named permission sets you can assign to sub-super-admins."
        actions={
          <Button asChild>
            <Link to="/create-acl-settings">
              <Plus className="h-4 w-4" />
              New role
            </Link>
          </Button>
        }
      />

      <DataTable<AclSettingRow, unknown>
        tableId="acl:settings:v1"
        columns={columns}
        data={rows}
        isLoading={list.isLoading}
        searchPlaceholder="Search roles…"
        emptyTitle="No roles"
        emptyDescription='Click "New role" to define your first ACL permission set.'
        getRowId={(row) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="acl-roles.csv"
        pageSize={25}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Delete role?"
        description={
          <>
            Role{' '}
            <span className="font-medium text-foreground">
              {pendingDelete?.aclName}
            </span>{' '}
            will be soft-deleted. Sub-super-admins assigned to it lose access
            on next login.
          </>
        }
        confirmLabel="Delete"
        tone="destructive"
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.id, {
            onSuccess: (res) => {
              toast.success(res?.message ?? `Deleted ${pendingDelete.aclName}`);
              setPendingDelete(null);
            },
            onError: () => toast.error('Could not delete the role.'),
          });
        }}
      />
    </FillPage>
  );
}
